import { HttpDownloadProgressEvent, HttpEventType, HttpUploadProgressEvent } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";

import { Observable, Subject } from 'rxjs';

import { v4 as uuidv4 } from 'uuid';

import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { RepositoryFolderService } from "@repositories/services";
import { map, mergeMap, takeUntil } from "rxjs/operators";
import { AuthenticationService, AuthenticationTypeService } from "@common/services";
import { InsightsService } from "@core/services";
import { CustomSwalService } from "app/helpers";
import { environment } from "environments/environment";
import { TranslateService } from "@ngx-translate/core";
import { checkInTeams, copyContent, getMimeType } from "@core/utils";
import { Content, FileContent, FileForViewer } from "@repositories/models";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import { FileService } from "@bpm/services";
import { app } from "@microsoft/teams-js";
import { Router } from "@angular/router";



@Injectable()
export class RepositoryService implements OnDestroy {

    private _unsubscribeAll: Subject<any>;

    constructor(
        private _authenticationService: AuthenticationService,
        private router: Router,
        private _fileService: FileService,
        private _log: InsightsService,
        private _repositoryFolderService: RepositoryFolderService,
        private _sanitizer: DomSanitizer,
        private _swalService: CustomSwalService,
        private _translateService: TranslateService,
        private _authenticationTypeService: AuthenticationTypeService
    ) {
        this._unsubscribeAll = new Subject();
        GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.worker.min.js';
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Función que sirve para abrir o descargar un archivo de la belivpedia
     * @param {string} fileName Indica el nombre del archivo a descargar
     * @param {string} url Indica la url del archivo a descargar
     * @param {Blob} blob Contenido del archivo
     * @param {string} visualizationUrl URL pública que lee el visor de Office. Por
     * defecto es la de belivpedia; los archivos de otros storages (p. ej. el de
     * bpm que usa el gestor de archivos) mandan la suya.
     * @returns Devuelve un objeto con el tipo de archivo y la url del archivo
     */
    async getUrlForViewer(fileName: string, url: string, blob: Blob, visualizationUrl?: string): Promise<FileForViewer> {
        let fileType: string = "",
            fileUrl: string | SafeResourceUrl = url;
        switch (fileName.split(".").pop().toLowerCase()) {
            case "pdf":
                fileType = "pdf";
                break;
            case "jpg":
            case "jpeg":
            case "bmp":
            case "png":
                fileType = "img";
                fileUrl = this._sanitizer.bypassSecurityTrustResourceUrl(url);
                break;
            case "mp4":
                fileType = "video";
                break;
            case "json":
            case "vtt":
            case "md":
            case "txt":
                fileUrl = this._sanitizer.bypassSecurityTrustResourceUrl(url);
                fileType = "txt";
                break;
            case "html":
                fileType = "html";
                fileUrl = this._sanitizer.bypassSecurityTrustResourceUrl(url);
                break;
            default:
                if (!visualizationUrl) {
                    // Getting key for office viewer authentication through key
                    const apiKey = await this._authenticationTypeService.getApiKey();

                    visualizationUrl = `${environment.repositoriesApiUrl}/api/files/visualize-blob/${fileName}?code=${apiKey}`;
                }
                const previewLink = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(visualizationUrl)}`;
                fileUrl = this._sanitizer.bypassSecurityTrustResourceUrl(previewLink);
                fileType = "document";
        }
        return {
            fileType: fileType,
            url: fileUrl,
            downloadUrl: this._sanitizer.bypassSecurityTrustResourceUrl(url),
            blob: blob
        };
    }

    /**
     * Función que sirve para descargar un archivo de la belivpedia
     * @param {string} fileName Indica el nombre del archivo a descargar
     * @param {string} title Indica el nombre real del archivo
     * @returns {Observable<any>} Devuelve una promesa con el archivo descargado
     */
    getFile(fileName: string, title: string): Observable<number | FileForViewer> {
        // Obtiene el archivo desde el azure storage
        return this._repositoryFolderService
            .downloadFileByName(fileName)
            .pipe(
                mergeMap(async(event) => {
                    switch (event.type) {
                        case HttpEventType.DownloadProgress:
                            const ev: HttpDownloadProgressEvent = event as HttpDownloadProgressEvent;
                            const itemPercent = ev.total ? Math.round(100 * ev.loaded / ev.total) : 0;
                            return itemPercent;
                        case HttpEventType.Response:
                            // Obtiene la extensión del archivo
                            const ext = fileName.split(".").pop();
                            // Obtiene su tipo de mime por medio de la extensión
                            const mimeType = getMimeType(`.${ext}`);
                            // Obtiene el archivo y su tipo de archivo respectivamente
                            const file = new File([event.body], `${title}.${ext}`, { type: mimeType ?? event.body.type });

                            // Crea una url local
                            const url = window.URL.createObjectURL(file);

                            this.log(`Descargó el archivo ${fileName} en belivpedia.`, fileName);

                            // Obtiene la url y el tipo de archivo para belivpedia
                            const fileViewer = await this.getUrlForViewer(fileName, url, event.body);
                            return fileViewer;
                    }
                }),
                takeUntil(this._unsubscribeAll));
    }

    /**
     * Función que sirve para descargar un archivo de la belivpedia
     * @param {string} fileName Indica el nombre del archivo a descargar
     * @param {string} title Indica el nombre real del archivo
     * @returns {Promise<void>} Devuelve una promesa
     */
    download(fileName: string, title: string): Promise<void> {
        return new Promise(async (resolve, reject) => {
            this._swalService.showLoading();

            // Obtiene el archivo desde el azure storage
            const file = await this.getFile(fileName, title)
                .pipe(takeUntil(this._unsubscribeAll))
                .toPromise()
                .catch(error => {
                    reject(error);
                    this.log(`No se encontró el archivo ${fileName} en belivpedia.`, fileName, error, true);
                }) as FileForViewer;

            // TODO: validar si descarga no es afectada al ejecutar dentro de Teams y luego borrar codigo comentado
            // Verifica si la aplicación se esta corriendo en teams
            // if (checkInTeams()) {
            //     // Inicializa el contexto de la app
            //     app.initialize().then(() => {
            //         // Abre el link con la url de descarga
            //         app.openLink(file.downloadUrl['changingThisBreaksApplicationSecurity'])
            //     });
            // } else {
            //     // Si la aplicación se esta corriendo en la web
            //     // Abre el archivo en una nueva pestaña
            //     window.open(file.downloadUrl['changingThisBreaksApplicationSecurity'], "_blank");
            // }

            // Descarga el archivo automaticamente desde el navegador
            this.downloadFromBlobUrl(file.downloadUrl['changingThisBreaksApplicationSecurity'], `${title}.${fileName.split('.').pop()}`);


            this._swalService.close()
            resolve();
        });
    }

    /**
     * Copia la url del archivo en el portapapeles
     * @param {FileContent} file Objeto de tipo File de Repository
     * @returns {Promise<void>} Devuelve una promesa
     */
    copyToClipboard(file: FileContent): Promise<void> {
        return new Promise(async (resolve, reject) => {
            // Verifica si el archivo trae tipo de contenido
            // Si no lo trae, lo obtiene desde el servicio
            if (!file.contentType) {
                file = await this._repositoryFolderService.getFile(file.id).toPromise();
            }
            // Obtiene el mensaje de la traducción
            this._translateService.get("COMMON.ACTIONS.COPIED").pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
                // Almacena el mensaje en el log
                this.log(`Copió la url del archivo ${file.title} en belivpedia.`, file);

                const decodedUrl = decodeURIComponent(this.router.url)
                    .replace(/\/repositories(?!\/shared-with-me)(?=\/|$)/, '/repositories/shared-with-me');

                const url = file.contentType == "URL" ? file.url : 
                    file.contentType == "#folder" ? `${window.location.origin}${decodedUrl}/${file.id}|${file.title}` : 
                    `${window.location.origin}/apps/repositories/view/${file.id}`;

                // Copia la url del archivo en el portapapeles
                copyContent(url);

                // Muestra el mensaje de éxito
                this._swalService.showSuccessMsg("", value);
                resolve();
            });
        });
    }

    /**
     * Función que sirve para subir un archivo a la belivpedia
     * @param {FileContent} fileInfo Indica el objeto de tipo FileContent
     * @param {File} file Indica el archivo a subir
     * @param {Content} content Indica el contenido al que pertenece el archivo
     * @param {File} imgFile Indica el archivo de imagen a subir
     * @returns {Promise<any>} Devuelve una promesa con el resultado de la subida
     */
    uploadFile(fileInfo: FileContent, file: File, content: Content = null, imgFile: File = null): Observable<any> {
        return this._repositoryFolderService.uploadBlobFile(file, fileInfo?.id)
            .pipe(
                mergeMap(async event => {
                    switch (event.type) {
                        case HttpEventType.UploadProgress:
                            return event;
                        case HttpEventType.Response:
                            // Si el archivo se subió correctamente, lo crea en belivpedia
                            const result = await this.createFile(fileInfo, file, event.body.fileName, content, imgFile);
                            this.log(`Se subió el archivo ${result.title} en belivpedia.`, result);
                            return result;
                    }
                }),
                takeUntil(this._unsubscribeAll)
            );
    }

    /**
     * Crea/Edita un archivo en belivpedia
     * @param {FileContent} fileInfo Indica el objeto de tipo FileContent
     * @param {File} file Indica el archivo a subir
     * @param {string} fileName Indica el nombre del archivo
     * @param {Content} content Indica el contenido al que pertenece el archivo
     * @param {File} imgFile Indica el archivo de imagen a subir
     * @returns {Promise<any>} Devuelve una promesa con el resultado de la creación/edición
     */
    async createFile(fileInfo: FileContent, file: File = null, fileName: string, content: Content, imgFile: File = null): Promise<any> {
        return new Promise(async (resolve, reject) => {
            // Verifica si el archivo trae tipo de contenido
            if (!fileInfo) {
                // Si no lo trae, lo crea un nuevo objeto de tipo FileContent
                fileInfo = this.newFileInfo(file);
            }

            // Verifica si trae un archivo o un archivo de imagen
            if (file || imgFile) {
                // Asigna la url de la miniatura
                fileInfo.img = await this.uploadThumbFile(file, fileInfo.contentType, imgFile);
            }

            // Si el archivo no trae información de ancestros y repositorio, se lo asigna
            if (content) {
                // Asigna los ancestros del archivo
                fileInfo.ancestors = content.ancestors;
                // Asigna el id del repositorio
                fileInfo.repository = content.repository;
            }

            // Asigna el nombre del archivo
            fileInfo.fileName = fileName;

            // Si tiene id, edita el archivo
            if (fileInfo.id) {
                // Crea el archivo dentro de belivpedia
                this._repositoryFolderService.editFileContent(fileInfo)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(
                        (res) => {
                            resolve(res);
                            this.log(`Se editó el archivo ${fileInfo.title} en belivpedia.`, fileInfo);
                        },
                        (error) => {
                            reject(error);
                            this.log(`No se pudo editar el archivo ${fileInfo.title} en belivpedia.`, fileInfo, error, true);
                        }
                    );
            } else {
                // Crea el archivo dentro de belivpedia
                this._repositoryFolderService.addFileContent(fileInfo)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(
                        (res) => {
                            resolve(res);
                            this.log(`Se creó el archivo ${fileInfo.title} en belivpedia.`, fileInfo);
                        },
                        (error) => {
                            reject(error);
                            this.log(`No se pudo crear el archivo ${fileInfo.title} en belivpedia.`, fileInfo, error, true);
                        }
                    );
            }
        });
    }

    /**
     * Función que sirve para subir un archivo de miniatura
     * @param {File} file Indica el archivo a subir
     * @param {string} contentType Indica el tipo de contenido del archivo
     * @param {File} imgFile Indica el archivo de imagen a subir
     * @returns {Promise<string>} Devuelve una promesa con la url de la miniatura
     */
    async uploadThumbFile(file: File, contentType: string, imgFile: File = null): Promise<string> {
        return new Promise(async (resolve, reject) => {
            let imgUrl: string = "";

            try {
                if (file && contentType == "PDF" && !imgFile) {
                    const pdfUrl = URL.createObjectURL(file);

                    try {
                        imgUrl = await this.generateThumb(pdfUrl);
                    } finally {
                        URL.revokeObjectURL(pdfUrl);
                    }
                } else if (file && file.type.includes("image") && !imgFile) {
                    imgUrl = await this.uploadThumb(file);
                } else if (imgFile) {
                    imgUrl = await this.uploadThumb(imgFile);
                }

                resolve(imgUrl);
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Función que sirve para generar la miniatura de un archivo PDF
     * @param {string} pdfUrl Indica la url del archivo pdf
     * @returns {Promise<string>} La url de la miniatura generada
     */
    async generateThumb(pdfUrl: string): Promise<string> {
        return new Promise(async (resolve, reject) => {

            // Obtiene el documento pdf
            const doc = await getDocument(pdfUrl).promise.catch(error => { reject(error); throw error; });

            // Obtiene la primera página del documento
            const page = await doc.getPage(1).catch(error => { reject(error); throw error; });

            // Crea un canvas para renderizar la miniatura
            const canvas = document.createElement("canvas");
            canvas.width = 500;

            // Definimos la escala de la miniatura
            const scale = (canvas.width / page.view[2]);

            // Definimos la altura de la miniatura
            const viewport = page.getViewport({ scale: scale });
            canvas.height = page.view[3] * scale;

            // Renderizamos la miniatura
            await page.render({ canvasContext: canvas.getContext("2d"), viewport: viewport }).promise.catch(error => { reject(error); throw error; });

            // Convertimos el canvas a un blob
            const image = canvas.toDataURL("image/png");
            // Convertimos el blob a un objeto File
            const newFile = this.dataURItoBlob(image, "thumb.png");

            // Cargamos la miniatura al azure storage
            const imageUrl: string = await this.uploadThumb(newFile).catch(error => { reject(error); throw error; });

            resolve(imageUrl);
        });
    }


    /**
     * Función que sirve para generar la miniatura de un archivo
     * @param {File} file Indica el archivo a subir
     * @returns {Promise<string>} Devuelve la url de la miniatura
     */
    async uploadThumb(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            // Obtiene la url de la miniatura
            this._repositoryFolderService.getUploadUrlThumb(file.name).pipe(takeUntil(this._unsubscribeAll))
                .subscribe(response => {
                    // Realiza la subida de la miniatura
                    this._repositoryFolderService.uploadFile(response.Url, file.type, file).pipe(takeUntil(this._unsubscribeAll))
                        .subscribe(res => {
                            resolve(response.fileUrl);
                        }, error => reject(error)
                        );
                }, error => reject(error));
        });
    }

    /**
     * Función para crear carpetas de manera rápida
     * @param {string} title Indica el nombre de la carpeta
     * @param {string} repository Indica el id del repositorio
     * @param {string[]} ancestors Indica los ancestros de la carpeta
     * @returns {Promise<FileContent>} Devuelve una promesa de creación de carpeta
     */
    async createFolder(title: string, repository: string, ancestors: string[]): Promise<FileContent> {
        return new Promise((resolve, reject) => {
            // Inicializa el file content de tipo carpeta
            let folder = new FileContent();

            // Agrega la información de la carpeta
            folder.repository = repository;
            folder.title = title;
            folder.contentType = '#folder';
            folder.fileName = `${uuidv4()}.folder`;
            folder.ancestors = [...ancestors];

            // Crea la carpeta en belivpedia
            this._repositoryFolderService.addFileContent(folder)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(res => {
                    resolve(res);
                    // Agrega el log de la creación de la carpeta
                    this.log(`Se creó la carpeta ${folder.title} correctamente en belivpedia.`, folder);
                }, error => {
                    reject(error);
                    this.log(`No se pudo crear la carpeta ${folder.title} en belivpedia.`, folder, error, true);
                });
        });
    }

    /**
     * Función que sirve para crear un nuevo objeto de tipo FileContent
     * @param {File} file Indica el archivo a subir
     * @returns {FileContent} Devuelve un objeto de tipo FileContent
     */
    public newFileInfo(file: File): FileContent {
        // Si no lo trae, creamos el contenido en base al archivo
        const fileInfo = new FileContent();

        delete fileInfo.id;
        // Asignamos el titulo del archivo
        fileInfo.title = file.name.replace(/\.[^/.]+$/, "");
        // Asignamos el tipo de contenido del archivo
        fileInfo.contentType = file.name.match(/\.[0-9a-z]+$/i)[0].replace(".", "").toUpperCase();

        // Si es uno de los siguientes tipos de archivo, lo asignamos como un tipo de contenido
        fileInfo.contentType = fileInfo.contentType.includes("XL") ? 'EXCEL' :
            fileInfo.contentType.includes("CSV") ? 'EXCEL' :
                fileInfo.contentType.includes("PP") ? 'POWERPOINT' :
                    fileInfo.contentType.includes("DOC") ? 'WORD' :
                        fileInfo.contentType.includes("PDF") ? 'PDF' : fileInfo.contentType;

        return fileInfo;
    }

    /**
     * Convierte un string en base 64 a un objeto File
     * @param dataURI string en base 64
     * @param fileName nombre que se le va a poner al File
     * @returns objeto File
     */
    private dataURItoBlob(dataURI: any, fileName: string): File {

        // convert base64/URLEncoded data component to a file
        var byteString;
        if (dataURI.split(',')[0].indexOf('base64') >= 0)
            byteString = atob(dataURI.split(',')[1]);
        else
            byteString = unescape(dataURI.split(',')[1]);

        // separate out the mime component
        var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

        // write the bytes of the string to a typed array
        var ia = new Uint8Array(byteString.length);
        for (var i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        return new File([ia], fileName, { type: mimeString });
    }


    /**
     * Automatically download a file from a blob url
     * @param blobUrl
     * @param fileName
     */
    private downloadFromBlobUrl(blobUrl: string, fileName: string) {

        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);

    }

    /**
     * Función que sirve para mostrar un mensaje en el log
     * @param message Indica el mensaje a mostrar en el log
     * @param data Indica los datos a mostrar en el log
     * @param ex Indica la excepcion a mostrar en el log
     * @param error Indica si es un error o no
     * @returns {void}
     */
    private log(message: string, data: any, ex: any = null, error: boolean = false): void {
        if (error) {
            this._log.traceError(environment.projects.Belivpedia, message, data, ex, this._authenticationService.currentUserValue);
            return;
        } else {
            this._log.traceInfo(environment.projects.Belivpedia, message, data, this._authenticationService.currentUserValue);
        }
    }

    checkExistingFile(file: File, content: Content): Observable<FileContent | null> {

        const fileInfo = this.newFileInfo(file);

        fileInfo.repository = content.repository;
        fileInfo.ancestors = content.ancestors;

        return this._repositoryFolderService.checkExistingFile(fileInfo);
    }
    checkExistingFolder(title: string, repository: string, ancestors: string[]): Observable<FileContent | null> {
        const folder = new FileContent();
        delete folder.id;
        folder.title = title;
        folder.contentType = '#folder';
        folder.repository = repository;
        folder.ancestors = [...ancestors];
        return this._repositoryFolderService.checkExistingFile(folder);
    }
}